import React, {useMemo} from 'react'
import {Heart} from 'hec/assets/icons'
import {useCustomerProductLists} from '@salesforce/commerce-sdk-react'
import {useLocation} from 'react-router-dom'
import useMediaQuery from 'hec/hooks/useMediaQuery'
import {useMutateCustomerProductList} from 'hec/hooks/commerce-sdk/useMutateCustomerProductList'
import {useProductContext} from '@/context/ProductContext'
import {cn} from '../../lib/utils'

const HEART_FILL_COLOR = '#CD3C5E'

export const ProductFavoritesButton = () => {
    const location = useLocation()
    const currentPath = location.pathname
    const isMobile = useMediaQuery('sm')
    const {
        addItemToProductList,
        addItemToProductListMutation,
        removeItemFromProductList,
        removeItemFromProductListMutation
    } = useMutateCustomerProductList()
    const {
        product: {id: productId},
        registeredCustomerId
    } = useProductContext()
    const isPendingUpdate =
        addItemToProductListMutation.isLoading || removeItemFromProductListMutation.isLoading

    const {data: customerProductLists, isLoading: isCustomerProductListsLoading} =
        useCustomerProductLists(
            {
                parameters: {customerId: registeredCustomerId || ''}
            },
            {enabled: !!registeredCustomerId}
        )

    const wishList = useMemo(
        () => customerProductLists?.data?.find((list) => list.type === 'wish_list'),
        [customerProductLists?.data]
    )
    const isFavorite = useMemo(
        () => !!wishList?.customerProductListItems?.find((item) => item.productId === productId),
        [wishList, productId]
    )

    const handleRemoveFavorite = async () => {
        try {
            await removeItemFromProductList({
                registeredCustomerId,
                productId,
                productList: wishList
            })
        } catch (e) {
            console.error('An error occurred while trying to add favorites: ', e)
        }
    }

    const handleAddFavorite = async () => {
        try {
            await addItemToProductList({registeredCustomerId, productId, productList: wishList})
        } catch (e) {
            console.error('An error occurred while trying to add favorites: ', e)
        }
    }

    const handleFavoritesClick = async () => {
        if (registeredCustomerId) {
            if (isFavorite) {
                await handleRemoveFavorite()
            } else {
                await handleAddFavorite()
            }
        }
    }

    if (isCustomerProductListsLoading && !!registeredCustomerId) {
        return
    }
    const heartButtonClasses =
        'absolute top-0 right-0 bg-transparent p-3 rounded-full cursor-pointer'
    if (registeredCustomerId) {
        return (
            <button className={heartButtonClasses} onClick={handleFavoritesClick} title="Wishlist">
                <Heart
                    fill={isFavorite ? HEART_FILL_COLOR : 'transparent'}
                    stroke={isFavorite ? HEART_FILL_COLOR : 'currentColor'}
                    width={isMobile ? 20 : 24}
                    height={isMobile ? 20 : 24}
                    className={cn(
                        'transition-opacity duration-200 ',
                        isPendingUpdate ? 'opacity-50' : ''
                    )}
                />
            </button>
        )
    }

    return (
        <a
            href="#login"
            className={heartButtonClasses}
            title="Wishlist"
            onClick={(e) => {
                e.preventDefault()
                // Store current path for redirect after login
                sessionStorage.setItem('redirectAfterLogin', currentPath)
                window.location.hash = 'login'
            }}
            data-testid="productFavoritesButton-LoginLink"
        >
            <Heart
                data-testid="productFavoritesButton-Guest-Heart"
                width={isMobile ? 20 : 24}
                height={isMobile ? 20 : 24}
                className={cn(
                    'transition-opacity duration-200 ',
                    isPendingUpdate ? 'opacity-50' : ''
                )}
            />
        </a>
    )
}
